import React, { useState } from 'react';
import {
  Alert, Autocomplete, Box, Button, Card, CardContent, Chip, Dialog,
  DialogActions, DialogContent, DialogTitle, IconButton, MenuItem, Stack,
  TextField, Tooltip, Typography,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Label as ClassifyIcon,
} from '@mui/icons-material';
import {
  AllocationTargetsService,
  ALLOCATION_DIMENSIONS,
  CATEGORY_SUGGESTIONS,
  type AllocationDimension,
  type AllocationTarget,
  type AllocationTargetLine,
} from '../../services/allocation-targets-service';

interface Props {
  targets: AllocationTarget[];
  onChanged: () => void;
  onOpenClassifications: () => void;
}

const dimensionLabel = (d: AllocationDimension) =>
  ALLOCATION_DIMENSIONS.find((x) => x.value === d)?.label ?? d;

/**
 * Define target mixes: N% per category per dimension.
 *
 * The 100% rule is validated here for immediate feedback AND by the backend
 * (the authority); a target that does not add up is not a target.
 */
export const TargetsTab: React.FC<Props> = ({ targets, onChanged, onOpenClassifications }) => {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<AllocationTarget | null>(null);
  const [name, setName] = useState('');
  const [dimension, setDimension] = useState<AllocationDimension>('asset_class');
  const [lines, setLines] = useState<AllocationTargetLine[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const openCreate = () => {
    setEditing(null);
    setName('');
    setDimension('asset_class');
    setLines([{ category: '', target_pct: '' }]);
    setDialogOpen(true);
  };

  const openEdit = (t: AllocationTarget) => {
    setEditing(t);
    setName(t.name);
    setDimension(t.dimension);
    setLines(t.lines.map((l) => ({ ...l })));
    setDialogOpen(true);
  };

  const setLine = (i: number, patch: Partial<AllocationTargetLine>) =>
    setLines((prev) => prev.map((l, j) => (j === i ? { ...l, ...patch } : l)));

  const sum = lines.reduce((a, l) => a + (Number(l.target_pct) || 0), 0);
  const sumOk = Math.abs(sum - 100) < 0.001;

  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      const body = {
        name,
        dimension,
        lines: lines.filter((l) => l.category && l.target_pct),
      };
      if (editing) await AllocationTargetsService.updateTarget(editing.id, body);
      else await AllocationTargetsService.createTarget(body);
      setDialogOpen(false);
      onChanged();
    } catch (e: any) {
      setError(e?.message ?? 'Failed to save target');
    } finally {
      setSaving(false);
    }
  };

  const remove = async (t: AllocationTarget) => {
    if (!window.confirm(`Delete target "${t.name}"? Holdings are untouched.`)) return;
    try {
      await AllocationTargetsService.deleteTarget(t.id);
      onChanged();
    } catch (e: any) {
      setError(e?.message ?? 'Failed to delete target');
    }
  };

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
        <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 640 }}>
          A target is the mix you intend to hold — e.g. 60% equity, 40% fixed
          income. Classify your symbols first so the drift and rebalance views
          know which bucket each holding belongs to.
        </Typography>
        <Stack direction="row" spacing={1}>
          <Button startIcon={<ClassifyIcon />} onClick={onOpenClassifications}>
            Classify symbols
          </Button>
          <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate}>
            New target
          </Button>
        </Stack>
      </Stack>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

      {targets.length === 0 ? (
        <Card variant="outlined">
          <CardContent>
            <Typography color="text.secondary">
              No targets yet. Create one per dimension you care about — most
              households start with an asset-class target like 60/40.
            </Typography>
          </CardContent>
        </Card>
      ) : (
        <Stack spacing={2}>
          {targets.map((t) => (
            <Card key={t.id} variant="outlined">
              <CardContent>
                <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                  <Box>
                    <Typography variant="h6">{t.name}</Typography>
                    <Stack direction="row" spacing={1} sx={{ mt: 0.5, flexWrap: 'wrap', gap: 0.5 }}>
                      <Chip size="small" label={dimensionLabel(t.dimension)} />
                      {t.lines.map((l) => (
                        <Chip
                          key={l.category} size="small" variant="outlined"
                          label={`${l.category} ${l.target_pct}%`}
                        />
                      ))}
                    </Stack>
                  </Box>
                  <Stack direction="row">
                    <Tooltip title="Edit">
                      <IconButton size="small" onClick={() => openEdit(t)}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete">
                      <IconButton size="small" onClick={() => void remove(t)}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Stack>
                </Stack>
              </CardContent>
            </Card>
          ))}
        </Stack>
      )}

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editing ? 'Edit target' : 'New allocation target'}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Stack direction="row" spacing={2}>
              <TextField
                label="Name" value={name} placeholder="60/40"
                onChange={(e) => setName(e.target.value)} fullWidth
              />
              <TextField
                select label="Dimension" value={dimension}
                onChange={(e) => setDimension(e.target.value as AllocationDimension)}
                sx={{ width: 180 }}
              >
                {ALLOCATION_DIMENSIONS.map((d) => (
                  <MenuItem key={d.value} value={d.value}>{d.label}</MenuItem>
                ))}
              </TextField>
            </Stack>

            {lines.map((l, i) => (
              <Stack key={i} direction="row" spacing={1} alignItems="center">
                <Autocomplete
                  freeSolo fullWidth size="small"
                  options={CATEGORY_SUGGESTIONS[dimension]}
                  value={l.category}
                  onInputChange={(_, v) => setLine(i, { category: v })}
                  onChange={(_, v) => setLine(i, { category: v ?? '' })}
                  renderInput={(params) => <TextField {...params} label="Category" />}
                />
                <TextField
                  label="%" size="small" value={l.target_pct} sx={{ width: 100 }}
                  onChange={(e) => setLine(i, { target_pct: e.target.value })}
                />
                <IconButton
                  size="small"
                  onClick={() => setLines((prev) => prev.filter((_, j) => j !== i))}
                  disabled={lines.length === 1}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Stack>
            ))}

            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Button
                size="small" startIcon={<AddIcon />}
                onClick={() => setLines((prev) => [...prev, { category: '', target_pct: '' }])}
              >
                Add category
              </Button>
              <Typography
                variant="body2"
                color={sumOk ? 'success.main' : 'warning.main'}
                fontWeight={600}
              >
                Total {sum}% {sumOk ? '' : '(must be 100%)'}
              </Typography>
            </Stack>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={() => void save()}
            disabled={saving || !name || !sumOk}
          >
            {saving ? 'Saving…' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
